/**
 * Entity Version Detection
 * Determines if entity uses v1 (old) or v2 (new) format
 * 
 * @module entityVersionDetector
 */

export type EntityVersion = 'v1' | 'v2';

/**
 * Base entity interface (minimal)
 */
interface BaseEntity {
  skeleton?: {
    version?: number;
    yjsDocument?: string;
    [key: string]: any;
  };
  entityType?: string;
  [key: string]: any;
}

/**
 * Detect entity version based on skeleton structure
 * 
 * Detection Logic:
 * 1. Check for explicit version: 2 → v2
 * 2. Check for architecture: 'unified' → v2
 * 3. Check for yjsStructure: 'universal' → v2
 * 4. Check for yjsDocument field → v2
 * 5. Check if entity type is v2-only → v2
 * 6. Default → v1 (all existing entities)
 * 
 * @param entity - Entity to check
 * @returns 'v1' or 'v2'
 */
export function detectEntityVersion(entity: BaseEntity): EntityVersion {
  // Check for explicit version field
  // @ts-ignore - version can be number or string
  if (entity.skeleton?.version === 2 || entity.skeleton?.version === 'v2' || entity.skeleton?.version === '2') {
    return 'v2';
  }
  
  // Check for architecture marker
  if (entity.skeleton?.architecture === 'unified') {
    return 'v2';
  }
  
  // Check for Y.js structure marker
  if (entity.skeleton?.yjsStructure === 'universal') {
    return 'v2';
  }
  
  // Check for v2 structure (yjsDocument field)
  if (entity.skeleton?.yjsDocument) {
    return 'v2';
  }
  
  // Check if entity type is v2-only (new apps that only exist in v2)
  // Page and RiverBot both use PageApplication with Y.XmlFragment('prosemirror') storage.
  const v2OnlyTypes = ['Canvas', 'Sheet', 'Spreadsheet', 'Presentation', 'Slides', 'Playbook', 'Page', 'RiverBot'];
  if (entity.entityType && v2OnlyTypes.includes(entity.entityType)) {
    return 'v2';
  }
  
  // Default to v1 (all existing entities)
  return 'v1';
}

/**
 * Check if entity can use v2 apps
 * 
 * @param entity - Entity to check
 * @returns true if entity is v2
 */
export function canUseV2App(entity: BaseEntity): boolean {
  return detectEntityVersion(entity) === 'v2';
}

/**
 * Check if entity must use v1 apps
 * 
 * @param entity - Entity to check
 * @returns true if entity is v1
 */
export function mustUseV1App(entity: BaseEntity): boolean {
  return detectEntityVersion(entity) === 'v1';
}

/**
 * Entity capabilities based on version
 */
export interface EntityCapabilities {
  version: EntityVersion;
  supportsUniversalCommands: boolean;
  supportsUniversalSchema: boolean;
  supportsComments: boolean;
  supportsCheckpoints: boolean;
  supportsSpatialQueries: boolean;
  supportsGridQueries: boolean;
}

/**
 * Get entity capabilities based on version
 * 
 * @param entity - Entity to check
 * @returns Capabilities object
 */
export function getEntityCapabilities(entity: BaseEntity): EntityCapabilities {
  const isV2 = canUseV2App(entity);
  
  return {
    version: isV2 ? 'v2' : 'v1',
    supportsUniversalCommands: isV2,
    supportsUniversalSchema: isV2,
    supportsComments: isV2,
    supportsCheckpoints: true,  // Both v1 and v2
    supportsSpatialQueries: isV2 && (
      entity.entityType === 'Canvas' || 
      entity.entityType === 'Presentation'
    ),
    supportsGridQueries: isV2 && entity.entityType === 'Sheet',
  };
}

/**
 * Check if any entities in array are v2
 */
export function hasV2Entities(entities: BaseEntity[]): boolean {
  if (!entities || entities.length === 0) return false;
  return entities.some(entity => detectEntityVersion(entity) === 'v2');
}

/**
 * Check if any entities in array are v1
 */
export function hasV1Entities(entities: BaseEntity[]): boolean {
  if (!entities || entities.length === 0) return false;
  return entities.some(entity => detectEntityVersion(entity) === 'v1');
}

/**
 * Get appropriate command IDs for entity set
 * Used by ToolSelector to determine which commands to show
 */
export function getCommandsForEntities(entities: BaseEntity[]): string[] {
  const commands: string[] = [];
  
  // Add universal commands for v2 entities
  if (hasV2Entities(entities)) {
    commands.push('entity_read', 'entity_write', 'entity_format', 'entity_export');
  }
  
  // Add v1 commands for v1 entities
  if (hasV1Entities(entities)) {
    commands.push('doc_read_v2', 'doc_write', 'doc_get_metadata');
  }
  
  return commands;
}

/**
 * Filter entities by version
 */
export function filterEntitiesByVersion(
  entities: BaseEntity[],
  version: 'v1' | 'v2'
): BaseEntity[] {
  return entities.filter(entity => detectEntityVersion(entity) === version);
}

/**
 * Get entity version stats for an array of entities
 * Useful for debugging and analytics
 */
export function getVersionStats(entities: BaseEntity[]): {
  v1Count: number;
  v2Count: number;
  total: number;
  v2Percentage: number;
} {
  const v1Entities = filterEntitiesByVersion(entities, 'v1');
  const v2Entities = filterEntitiesByVersion(entities, 'v2');
  const total = entities.length;
  
  return {
    v1Count: v1Entities.length,
    v2Count: v2Entities.length,
    total,
    v2Percentage: total > 0 ? Math.round((v2Entities.length / total) * 100) : 0
  };
}

